import React, { useEffect, useState } from 'react';
import { Bell, Search, Sun, Moon, User, LogOut, Settings as SettingsIcon, ChevronDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useNavigate } from 'react-router-dom';
import { initials, formatDateTime } from '../utils/format';
import api from '../services/api';

export default function Header({ onOpenPalette }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [notifications, setNotifications] = useState({ items: [], unread: 0 });

  const loadNotifs = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
  }, []);

  const markAllRead = async () => {
    await api.post('/notifications/mark-all-read');
    loadNotifs();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/clientes?q=${encodeURIComponent(search)}`);
  };

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-neutral-200 dark:border-neutral-800 bg-white/85 dark:bg-neutral-950/85 backdrop-blur">
      <div className="h-full px-4 md:px-6 flex items-center gap-3">
        {/* Search - opens Command Palette (Ctrl+K) */}
        <button onClick={onOpenPalette} className="flex-1 max-w-lg relative h-10 pl-10 pr-3 rounded-lg bg-neutral-100 dark:bg-neutral-900 border border-transparent hover:border-neutral-200 dark:hover:border-neutral-700 text-sm text-left text-neutral-500 transition-colors flex items-center justify-between">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <span>Pesquisar módulos, telas, dados...</span>
          <span className="hidden md:inline text-[10px] font-mono bg-neutral-200 dark:bg-neutral-800 rounded px-1.5 py-0.5">Ctrl K</span>
        </button>

        <div className="flex items-center gap-1">
          {/* Theme toggle */}
          <button
            onClick={toggle}
            className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          {/* Notifications */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative h-10 w-10 flex items-center justify-center rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                <Bell className="h-5 w-5" />
                {notifications.unread > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-brand ring-2 ring-white dark:ring-neutral-950" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
                <p className="font-semibold text-sm">Notificações</p>
                {notifications.unread > 0 && (
                  <button onClick={markAllRead} className="text-xs text-brand hover:underline">Marcar todas como lidas</button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.items.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-neutral-500">Nenhuma notificação</div>
                ) : (
                  notifications.items.map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 ${!n.read ? 'bg-red-50/50 dark:bg-red-950/20' : ''}`}>
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-neutral-400 mt-1">{formatDateTime(n.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-10 pl-1 pr-2 flex items-center gap-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                <div className="h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-xs font-semibold">
                  {initials(user?.name)}
                </div>
                <div className="hidden md:flex flex-col items-start leading-tight">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-[11px] text-neutral-500">{user?.role}</span>
                </div>
                <ChevronDown className="hidden md:block h-4 w-4 text-neutral-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-xs text-neutral-500 font-normal">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/perfil')}>
                <User className="h-4 w-4 mr-2" /> Meu Perfil
              </DropdownMenuItem>
              {user?.role === 'Administrador' && (
                <DropdownMenuItem onClick={() => navigate('/configuracoes')}>
                  <SettingsIcon className="h-4 w-4 mr-2" /> Configurações
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-700">
                <LogOut className="h-4 w-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
